import { animate, createTimeline, stagger, spring } from '../../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, resolveDuration, resolveStagger, BLUR, SUPPORTS_CLIP } from '../motion-tokens.js';

export const countdownAnim = (el) => {
    const digits = el.querySelectorAll('.countdown-value, .countdown-digit, [class*="countdown-num"]');
    const labels = el.querySelectorAll('.countdown-label, [class*="countdown-label"]');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    // Primario: cada dígito cae como slot machine (translateY de arriba, varias iteraciones)
    if (digits.length) {
        tl.add(digits, {
            opacity:    [0, 1],
            translateY: [-40, 0],
            filter:     ['blur(4px)', 'blur(0px)'],
            delay:      stagger(resolveStagger(STAGGER.fast)),
            duration:   resolveDuration(DURATION.normal),
            ease:       EASE.bouncy,
        });

        // Flash sutil de box-shadow al asentarse (solo UNA vez — spec)
        tl.add(digits, {
            boxShadow: [
                '0 0 0px rgba(var(--accent-rgb, 99,102,241), 0)',
                '0 0 20px rgba(var(--accent-rgb, 99,102,241), 0.2)',
                '0 0 0px rgba(var(--accent-rgb, 99,102,241), 0)',
            ],
            duration: 700,
            ease:    'inOutSine',
        }, '-=200');
    }

    // Secundario: labels aparecen debajo
    if (labels.length) {
        tl.add(labels, {
            opacity:    [0, 1],
            translateY: [8, 0],
            delay:      stagger(resolveStagger(STAGGER.fast)),
            duration:   resolveDuration(DURATION.normal),
        }, '-=300');
    }

    return tl;
};

export const comparisonAnim = (el) => {
    // PRIMARIO: checkmarks aparecen con spring micro-bounce uno a uno.
    // SECUNDARIO: columnas entran con stagger.
    const cols   = el.querySelectorAll('.comparison-col, .comparison-column, th');
    const checks = el.querySelectorAll('.check-icon, .check-mark, [class*="check"], td svg, td .icon');
    const tl = createTimeline({ defaults: { ease: EASE.snappy }, });

    // Secundario: columnas caen con stagger
    if (cols.length) {
        tl.add(cols, {
            opacity:    [0, 1],
            translateY: [-20, 0],
            filter:     ['blur(3px)', 'blur(0px)'],
            delay:      stagger(resolveStagger(STAGGER.fast)),
            duration:   resolveDuration(DURATION.normal),
        });
    }

    // Primario: checkmarks aparecen uno a uno con spring elastic
    if (checks.length) {
        tl.add(checks, {
            opacity:    [0, 1],
            scale:      [0, 1],
            delay:      stagger(resolveStagger(50), { start: 0 }), // 50ms secuencial
            duration:   resolveDuration(DURATION.fast),
            ease:       spring({ stiffness: 400, damping: 10, mass: 0.5 }),
        }, cols.length ? '-=400' : 0);
    }

    return tl;
};

export const beforeAfterAnim = (el) => {
    // PRIMARIO: slider handle rebota al centro con spring.
    // SECUNDARIO: ambas imágenes entran desde lados opuestos con clipPath.
    const imgBefore = el.querySelector('.before-img, .before, [class*="before"] img, [class*="before"]');
    const imgAfter  = el.querySelector('.after-img, .after, [class*="after"] img, [class*="after"]');
    const handle    = el.querySelector('.slider-handle, .handle, [class*="handle"], [class*="drag"]');
    const tl = createTimeline({ defaults: { ease: EASE.snappy }, });

    // ESTADO DE REPOSO del comparador: el recorte que deja la mitad derecha a la vista.
    // Lo escribe el render (`clip-path:inset(0 50% 0 0)` inline en `.ba-before`) y lo reafirma
    // `interactive.js::initBeforeAfterSlider` con su `setPos(50)`; se lee de ahí en vez de
    // repetir el 50% para que, si la posición inicial se vuelve configurable, la animación
    // siga aterrizando donde toca.
    // POR QUÉ IMPORTA: esta coreografía terminaba en `inset(0 0% 0 0)` — sin recorte—, así que
    // al entrar el bloque la capa "antes" acababa TAPANDO la de "después" a pantalla completa,
    // con la línea divisoria clavada en el centro y nada que comparar. Solo reaparecía al
    // arrastrar, porque el arrastre reescribe el clip. El bug se veía únicamente en el sitio
    // publicado y en "Ver Sitio" (en edición no se anima).
    const clipReposo = (imgBefore && imgBefore.style.clipPath) || 'inset(0 50% 0 0)';

    // Secundario: ambas imágenes desde lados opuestos en paralelo (offset 0)
    if (imgBefore) {
        if (SUPPORTS_CLIP) {
            tl.add(imgBefore, {
                clipPath:  ['inset(0 100% 0 0)', clipReposo],
                opacity:   [0, 1],
                duration:  resolveDuration(DURATION.slow),
            }, 0);
        } else {
            tl.add(imgBefore, {
                opacity:    [0, 1],
                translateX: [-60, 0],
                duration:   resolveDuration(DURATION.slow),
            }, 0);
        }
    }

    if (imgAfter) {
        if (SUPPORTS_CLIP) {
            tl.add(imgAfter, {
                clipPath:  ['inset(0 0% 0 100%)', 'inset(0 0% 0 0%)'],
                opacity:   [0, 1],
                duration:  resolveDuration(DURATION.slow),
            }, 0);
        } else {
            tl.add(imgAfter, {
                opacity:    [0, 1],
                translateX: [60, 0],
                duration:   resolveDuration(DURATION.slow),
            }, 0);
        }
    }

    // Primario: handle rebota desde el borde izquierdo al centro
    if (handle) {
        tl.add(handle, {
            opacity:    [0, 1],
            translateX: [-60, 0],
            duration:   resolveDuration(DURATION.normal),
            ease:       spring({ stiffness: 250, damping: 14, mass: 1 }),
        }, '-=500'); // overlap: llega cuando las imágenes todavía están entrándose
    }

    return tl;
};

export const videoAnim = (el) => {
    // PRIMARIO: play button pulsa con scale loop sutil (loop infinito).
    // SECUNDARIO: container reveal con clipPath.
    const container = el.querySelector('.video-container, .video-wrapper, [class*="video"]');
    const playBtn   = el.querySelector('.play-btn, .play-button, [class*="play"]');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    // Secundario: container se revela con clipPath
    if (container) {
        if (SUPPORTS_CLIP) {
            tl.add(container, {
                clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'],
                opacity:  [0, 1],
                duration: resolveDuration(DURATION.slow),
                ease:     EASE.snappy,
            });
        } else {
            tl.add(container, {
                opacity:    [0, 1],
                scale:      [0.95, 1],
                filter:     ['blur(4px)', 'blur(0px)'],
                duration:   resolveDuration(DURATION.slow),
            });
        }
    }

    // Primario: play button aparece
    if (playBtn) {
        // 1. Aparece con bounce
        tl.add(playBtn, {
            opacity:  [0, 1],
            scale:    [0, 1],
            duration: resolveDuration(DURATION.normal),
            ease:     spring({ stiffness: 300, damping: 12, mass: 0.8 }),
        }, '-=400');
    }

    return tl;
};

/**
 * CARRUSEL — entrada de la banda rotativa (clave SIN variante: v1..v4 la comparten).
 *
 * Registrarla es OBLIGATORIO, no cosmético: una variante sin entrada en VARIANT_ANIMATIONS
 * cae al fallback genérico del orquestador, que puede congelar un tween y dejar el titular
 * semi-invisible PARA SIEMPRE en el sitio publicado (gotcha nº18).
 *
 * Solo se anima la PRIMERA diapositiva: las demás las gobierna Splide (y en `loop` hay
 * clones). Y NUNCA se toca el `transform` de la <img>: el zoom lento de la v1 es una
 * animación CSS sobre ese mismo transform, y dos escritores sobre la misma propiedad es
 * exactamente cómo se congela un tween.
 */
export const carruselAnim = (el) => {
    // El primer slide REAL (en `loop`, Splide antepone clones al montar).
    const slides = [...el.querySelectorAll('.carrusel-slide')]
        .filter(s => !s.classList.contains('splide__slide--clone'));
    const primero = slides[0] || el.querySelector('.carrusel-slide');
    const media = primero && primero.querySelector('.carrusel-media');
    const titulo = primero && primero.querySelector('.carrusel-title');
    const cta = primero && primero.querySelector('.carrusel-cta');
    const tira = el.querySelector('.carrusel-thumbs');
    const tl = createTimeline({ defaults: { ease: EASE.refined } });

    // PRIMARIO: la foto se destapa (cortina izq→der; fade donde no haya clip-path).
    if (media) {
        tl.add(media, SUPPORTS_CLIP ? {
            clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
            opacity:  [0, 1],
            duration: resolveDuration(DURATION.slow),
            ease:     EASE.snappy,
        } : {
            opacity:  [0, 1],
            filter:   [BLUR.heavy, BLUR.none],
            duration: resolveDuration(DURATION.slow),
        }, 0);
    }

    // SECUNDARIO: el titular sube.
    if (titulo) {
        tl.add(titulo, {
            opacity:    [0, 1],
            translateY: [26, 0],
            filter:     [BLUR.light, BLUR.none],
            duration:   resolveDuration(DURATION.normal),
            ease:       SPRING_PRESETS.deluxe,
        }, media ? '-=520' : 0);
    }

    // TERCIARIO: el botón entra al final (y solo si de verdad hay botón).
    if (cta && cta.textContent.trim()) {
        tl.add(cta, {
            opacity:    [0, 1],
            translateY: [14, 0],
            duration:   resolveDuration(DURATION.fast),
        }, '-=320');
    }

    // v4: la tira de miniaturas aparece bajo la banda.
    if (tira) {
        tl.add(tira, {
            opacity:    [0, 1],
            translateY: [18, 0],
            duration:   resolveDuration(DURATION.normal),
        }, '-=400');
    }

    return tl;
};

/**
 * IMAGEN — entrada de la foto suelta (clave SIN variante: v1 y v2 la comparten).
 *
 * Registrarla es OBLIGATORIO, no cosmético: una variante sin entrada en VARIANT_ANIMATIONS cae
 * al fallback genérico del orquestador, que puede congelar un tween y dejar la pieza
 * semi-invisible PARA SIEMPRE en el sitio publicado (gotcha nº18).
 *
 * Deliberadamente sobria: el contenido del bloque ES la foto, así que se destapa y ya. El botón
 * solo entra si el cliente lo ha añadido — sin `cta` el render no emite ancla ninguna.
 */
export const imagenAnim = (el) => {
    const marco = el.querySelector('.imagen-marco');
    // Con botón secundario entra la FILA de los dos (utils/boton-secundario.js); sin él, el botón.
    const cta = el.querySelector('.imagen-cta-wrap .n3i-botones, .imagen-cta');
    const tl = createTimeline({ defaults: { ease: EASE.refined } });

    if (marco) {
        tl.add(marco, SUPPORTS_CLIP ? {
            clipPath: ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'],   // cortina de arriba a abajo
            opacity:  [0, 1],
            duration: resolveDuration(DURATION.slow),
            ease:     EASE.snappy,
        } : {
            opacity:  [0, 1],
            filter:   [BLUR.heavy, BLUR.none],
            duration: resolveDuration(DURATION.slow),
        }, 0);
    }

    if (cta && cta.textContent.trim()) {
        tl.add(cta, {
            opacity:    [0, 1],
            translateY: [14, 0],
            duration:   resolveDuration(DURATION.fast),
        }, '-=320');
    }

    return tl;
};

export const newsletterAnim = (el) => {
    const title  = el.querySelector('h2, h3, .newsletter-title');
    const desc   = el.querySelector('p, .newsletter-desc, .subtitle');
    const form   = el.querySelector('form, .newsletter-form, .form-wrapper');
    const inputs = el.querySelectorAll('input, .btn-action, .form-field, form button');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    // Estado inicial forzado (previene FOVC)
    if (title) { title.style.opacity = '0'; title.style.transform = 'translateY(20px)'; title.style.filter = BLUR.normal; }
    if (desc)  { desc.style.opacity = '0'; desc.style.transform = 'translateY(15px)'; }
    inputs.forEach(i => { i.style.opacity = '0'; i.style.transform = 'translateY(10px)'; });

    // Título fade-up
    if (title) tl.add(title, {
        opacity:    [0, 1],
        translateY: [20, 0],
        filter:     [BLUR.normal, BLUR.none],
        duration:   resolveDuration(DURATION.slow),
    });

    // Descripción
    if (desc) tl.add(desc, {
        opacity:    [0, 1],
        translateY: [15, 0],
        duration:   resolveDuration(DURATION.normal),
    }, '-=600');

    // Form fields escalonados
    if (inputs.length) tl.add(inputs, {
        opacity:    [0, 1],
        translateY: [10, 0],
        delay:      stagger(resolveStagger(STAGGER.fast)),
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.magnetic,
    }, '-=400');

    return tl;
};

export const footerAnim = (el) => {
    const footer = el.querySelector('footer, section');
    if (!footer) return null;

    // Estado inicial forzado
    footer.style.opacity = '0';

    return animate(footer, {
        opacity:  [0, 1],
        duration: resolveDuration(DURATION.slow),
        ease:     EASE.refined,
    });
};

// ── TRUST — sellos de garantía entran con stagger fino ──
export const trustAnim = (el) => {
    const badges = el.querySelectorAll('.trust-badge');
    if (!badges.length) return null;
    badges.forEach((b) => { b.style.opacity = '0'; b.style.transform = 'translateY(16px)'; b.style.filter = 'blur(3px)'; });
    animate(badges, {
        opacity:    [0, 1],
        translateY: [16, 0],
        filter:     ['blur(3px)', 'blur(0px)'],
        delay:      stagger(resolveStagger(STAGGER.fast)),
        duration:   resolveDuration(DURATION.normal),
        ease:       EASE.refined,
    });
    return null;
};

// ── REVIEWS — cabecera + estrellas de la nota media (pop) + tarjetas en cascada ──
export const reviewsAnim = (el) => {
    const head  = el.querySelector('.rv-head');
    const stars = el.querySelectorAll('.rv-aggregate .rv-star');
    const cards = el.querySelectorAll('.rv-card');
    const tl = createTimeline({ defaults: { ease: EASE.refined }, });

    if (head) {
        head.style.opacity = '0'; head.style.transform = 'translateY(16px)';
        tl.add(head, {
            opacity:    [0, 1],
            translateY: [16, 0],
            duration:   resolveDuration(DURATION.normal),
        });
    }
    if (stars.length) {
        stars.forEach((s) => { s.style.opacity = '0'; });
        tl.add(stars, {
            opacity:  [0, 1],
            scale:    [0.4, 1],
            delay:    stagger(resolveStagger(40)),
            duration: resolveDuration(DURATION.fast),
            ease:     SPRING_PRESETS.magnetic,
        }, '-=150');
    }
    if (cards.length) {
        cards.forEach((c) => { c.style.opacity = '0'; c.style.transform = 'translateY(20px)'; c.style.filter = 'blur(3px)'; });
        tl.add(cards, {
            opacity:    [0, 1],
            translateY: [20, 0],
            filter:     ['blur(3px)', 'blur(0px)'],
            delay:      stagger(resolveStagger(STAGGER.normal)),
            duration:   resolveDuration(DURATION.normal),
        }, '-=200');
    }
    return tl;
};
